import { Pencil, Trash2, ClipboardList } from 'lucide-react';
import React, { useState, useEffect, useMemo } from 'react';
import { visiteAPI, mediciAPI, prestazioniAPI } from '../../services/api';
import SelettorePrestazione from './SelettorePrestazione';
import api from '../../services/api';
import ModuloAttestazione from './ModuloAttestazione';
import { templateAPI } from '../../services/templateApi';
import { imponibileRiga, ivaSu, somma } from '../../utils/denaro';
import { tracciabilitaAPI } from '../../services/tracciabilitaApi';
import ProceduraDettaglio from './ProceduraDettaglio';
import { Package } from 'lucide-react';
import MappaFiller from './MappaFiller';
import MappaBotox from './MappaBotox';
import MedicoSelect from '../MedicoSelect';
import FirmaConsenso from './FirmaConsenso';
import SceltaFirma from './SceltaFirma';
import { PannelloRiservato, RichiesteInAttesa } from './AccessoRiservato';
import { useAuthStore } from '../../store/useStore';
import '../../styles/Visite.css';
import '../../styles/FirmaConsenso.css';
import { annunciaProcedureCambiate } from '../../utils/annunci';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';
import { notificaErrore, messaggioDa } from '../../utils/notifiche';

/* Il nome dell'esecutore imposto, dall'elenco dei medici gia' caricato.
   Se l'elenco non e' ancora arrivato si mostra un ripiego, non un vuoto. */
const nomeMedico = (medici, id) => {
    const m = (medici || []).find((x) => String(x.id) === String(id));
    return m ? `${m.cognome || ''} ${m.nome || ''}`.trim() : 'il tuo profilo medico';
};

const VisiteTab = ({ pazienteId, focusVisitaId, sessoPaziente }) => {
    const utente = useAuthStore((state) => state.user);

    // Procedure riservate aperte in questa sessione, con il dettaglio
    // completo restituito dal backend.
    const [visiteAperte, setVisiteAperte] = useState({});

    /* Le richieste di autorizzazione che riguardano questo paziente, dalle
       due parti: quelle che devo decidere io (ho eseguito la procedura) e
       quelle che ho chiesto io e aspettano risposta. Si caricano una volta
       e si indicizzano per visita: una chiamata invece di una per riga. */
    const [daDecidere, setDaDecidere] = useState({});
    const [mieInAttesa, setMieInAttesa] = useState({});

    /* Apre una procedura riservata per chi e' autorizzato a vederla.
       Il 403 non e' un guasto: e' la risposta a chi deve prima chiedere,
       e il pannello che lo spiega e' gia' a schermo. */
    const apriRiservata = async (id) => {
        try {
            const r = await visiteAPI.apriRiservata(id);
            setVisiteAperte((p) => ({ ...p, [id]: r.data?.data?.visita }));
        } catch (e) {
            if (e?.status !== 403 && e?.response?.status !== 403) {
                console.error('Errore apertura procedura riservata:', e);
            }
        }
    };

    const caricaRichieste = async () => {
        try {
            const r = await visiteAPI.richiesteAccesso();
            const perVisita = (righe, filtro) => {
                const mappa = {};
                (righe || []).filter(filtro).forEach((x) => {
                    mappa[x.visita_id] = [...(mappa[x.visita_id] || []), x];
                });
                return mappa;
            };
            setDaDecidere(perVisita(r.data?.data?.da_decidere, () => true));
            const mie = perVisita(r.data?.data?.mie, (x) => x.stato === 'in_attesa');
            setMieInAttesa(mie);
        } catch (e) {
            // Un medico senza autorizzazioni pendenti non deve vedere errori.
            setDaDecidere({});
            setMieInAttesa({});
        }
    };

    /* Dopo una decisione la riga sparisce dall'elenco senza ricaricare
       tutto: la procedura e' aperta a schermo e un ricaricamento la
       richiuderebbe sotto le mani di chi ha appena deciso. */
    const dopoDecisione = (richiestaId) => {
        setDaDecidere((p) => {
            const nuovo = {};
            Object.entries(p).forEach(([k, v]) => {
                const resto = v.filter((x) => x.id !== richiestaId);
                if (resto.length) nuovo[k] = resto;
            });
            return nuovo;
        });
    };

    const [visite, setVisite] = useState([]);
    /* Le due cartelle del paziente. Si parte da «prestazioni mediche»
       perche' e' quella che hanno tutti: aprire su una cartella che a
       questo medico non compare sarebbe una schermata vuota all'arrivo. */
    const [cartella, setCartella] = useState('mediche');
    /* La cartella in cui nasce la procedura che si sta creando. Parte da
       quella aperta - e' quasi sempre quella giusta - ma si sceglie a
       mano: dedurla dalla linguetta e basta vuol dire che chi apre il
       modulo dalla cartella sbagliata se ne accorge a procedura salvata,
       quando la ritrova dove non la cerca. */
    const [cartellaNuova, setCartellaNuova] = useState('mediche');
    const [medici, setMedici] = useState([]);
    const [prestazioniCatalogo, setPrestazioniCatalogo] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNewVisitaForm, setShowNewVisitaForm] = useState(false);
    const [expandedVisita, setExpandedVisita] = useState(null);
    /* Visita appena salvata che consuma prodotti a lotto obbligatorio: il
       pannello del numero di lotto compare subito dopo il salvataggio,
       finche' la scatola e' ancora sul tavolo. Chiederlo domani vuol dire
       ricostruirlo a memoria. */
    /* Vero quando la procedura appena salvata ha prodotti a lotto
       obbligatorio: si avvisa il medico di consegnare il tagliando, non
       gli si chiede il numero. */
    const [materialeInCoda, setMaterialeInCoda] = useState(false);
    const [moduliPerVisita, setModuliPerVisita] = useState({});
    const [fillerData, setFillerData] = useState({ valori: {}, zones: [] });
    const [botoxData, setBotoxData] = useState({ valori: {}, punti: [] });

    /* Chi e' collegato a un medico e' l'esecutore, punto: il server
       ignora qualunque medico_id gli si mandi (services/esecutore.js).
       Qui il campo diventa di sola lettura, perche' chi salva veda a
       nome di chi sta registrando - non e' una protezione, e' un'informazione. */
    const esecutoreImposto = !!(utente && utente.medico_id);

    // Stato del form di nuova procedura (controllato)
    const [formMedicoId, setFormMedicoId] = useState(esecutoreImposto ? utente.medico_id : null);
    const [formPrestazioni, setFormPrestazioni] = useState([]);

    // Consensi richiesti dalle prestazioni selezionate
    const [consensiDisponibili, setConsensiDisponibili] = useState([]);
    const [attestazioniValide, setAttestazioniValide] = useState([]);
    // Attestazione da compilare prima di salvare: { indice, nome }
    const [attestazioneDaFare, setAttestazioneDaFare] = useState(null);
    // null = si deve ancora scegliere, 'pc' = si firma qui
    const [dovesiFirma, setDoveSiFirma] = useState(null);
    const [datiVisitaInAttesa, setDatiVisitaInAttesa] = useState(null);
    const [firmaConsenso, setFirmaConsenso] = useState(true);
    const [consensoDaFirmare, setConsensoDaFirmare] = useState(null);

    useEffect(() => {
        loadVisite();
        loadMedici();
        loadPrestazioni();
        loadAttestazioni();
        caricaRichieste();
        // eslint-disable-next-line
    }, [pazienteId]);

    // apertura automatica di una procedura richiesta da Timeline/Recap
    useEffect(() => {
        if (focusVisitaId && !loading) {
            setExpandedVisita(focusVisitaId);
            if (!moduliPerVisita[focusVisitaId]) {
                loadModuli(focusVisitaId);
            }
            setTimeout(() => {
                const el = document.getElementById('visita-' + focusVisitaId);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 150);
        }
        // eslint-disable-next-line
    }, [focusVisitaId, loading]);

    // Ricerca dei consensi associati alle prestazioni scelte
    useEffect(() => {
        let attivo = true;

        const cerca = async () => {
            const ids = formPrestazioni
                .map((p) => p.prestazione_id)
                .filter(Boolean);

            if (ids.length === 0) {
                if (attivo) setConsensiDisponibili([]);
                return;
            }

            try {
                const trovati = [];
                const visti = new Set();

                for (const riga of formPrestazioni) {
                    if (!riga.prestazione_id) continue;
                    const r = await templateAPI.getPerPrestazione(riga.prestazione_id);
                    (r.data.data.template || []).forEach((t) => {
                        if (visti.has(t.id)) return;
                        visti.add(t.id);
                        trovati.push({ ...t, _prestazione: riga.nome_snapshot });
                    });
                }

                if (attivo) setConsensiDisponibili(trovati);
            } catch (e) {
                console.error('Errore ricerca consensi:', e);
                if (attivo) setConsensiDisponibili([]);
            }
        };

        cerca();
        return () => { attivo = false; };
    }, [formPrestazioni]);

    const loadVisite = async () => {
        try {
            setLoading(true);
            const response = await visiteAPI.getByPazienteId(pazienteId);
            if (response.data.success) {
                setVisite(response.data.data.visite);
            }
        } catch (error) {
            console.error('Errore caricamento procedure:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadMedici = async () => {
        try {
            const response = await mediciAPI.getAll(true);
            if (response.data.success) {
                setMedici(response.data.data.medici || []);
            }
        } catch (error) {
            console.error('Errore caricamento medici:', error);
        }
    };

    // Attestazioni gia' firmate e ancora valide: un ciclo di sedute
    // si appoggia alla stessa, non se ne rifa' una per ogni seduta.
    const loadAttestazioni = async () => {
        try {
            const r = await api.get(`/attestazioni/valide/${pazienteId}`);
            setAttestazioniValide(r.data.data.attestazioni || []);
        } catch (error) {
            console.error('Errore caricamento attestazioni:', error);
        }
    };

    const loadPrestazioni = async () => {
        try {
            const response = await prestazioniAPI.getAll(true);
            if (response.data.success) {
                setPrestazioniCatalogo(response.data.data.prestazioni || []);
            }
        } catch (error) {
            console.error('Errore caricamento prestazioni:', error);
        }
    };

    const loadModuli = async (visitaId) => {
        try {
            const response = await visiteAPI.getModuli(visitaId);
            if (response.data.success) {
                setModuliPerVisita(prev => ({
                    ...prev,
                    [visitaId]: response.data.data.moduli
                }));
            }
        } catch (error) {
            console.error('Errore caricamento moduli:', error);
        }
    };

    // Prestazioni disponibili nel form: filtro per medico selezionato
    const prestazioniDisponibili = useMemo(() => {
        // Solo servizi: i prodotti si vendono in cassa, qui il medico
        // registra cio' che ha eseguito.
        const soloServizi = prestazioniCatalogo.filter(
            (p) => !p.tipologia || p.tipologia === 'servizio'
        );
        if (!formMedicoId) return soloServizi;
        return soloServizi.filter(
            (p) => !p.medico_id || String(p.medico_id) === String(formMedicoId)
        );
    }, [prestazioniCatalogo, formMedicoId]);

    // Auto-rilevamento Botox/Filler dai nomi delle prestazioni selezionate
    const isBotox = formPrestazioni.some((p) =>
        (p.nome_snapshot || '').toLowerCase().includes('botox')
    );
    const isFiller = formPrestazioni.some((p) =>
        (p.nome_snapshot || '').toLowerCase().includes('filler')
    );

    /* Totali del form. Si calcolano riga per riga in centesimi interi, nello
       stesso ordine in cui li rifara' la fattura: e' l'unico modo perche' il
       numero letto al paziente prima di salvare sia quello che poi finisce
       stampato. Sommando in virgola mobile lo 0,8% delle righe mostrava un
       centesimo di IVA in meno. */
    const totaliForm = useMemo(() => {
        let imponibile = 0;
        let iva = 0;
        for (const p of formPrestazioni) {
            const riga = imponibileRiga(p.prezzo_unitario, parseFloat(p.quantita) || 0);
            imponibile = somma(imponibile, riga);
            iva = somma(iva, ivaSu(riga, p.iva));
        }
        return { imponibile, iva, totale: somma(imponibile, iva) };
    }, [formPrestazioni]);

    const handleAddPrestazioneRow = () => {
        setFormPrestazioni((prev) => [
            ...prev,
            { prestazione_id: '', nome_snapshot: '', prezzo_unitario: 0, iva: 0, quantita: 1, costo_prodotto_snapshot: 0 },
        ]);
    };

    const handleRemovePrestazioneRow = (index) => {
        setFormPrestazioni((prev) => prev.filter((_, i) => i !== index));
    };

    const handlePrestazioneChange = (index, prestazioneId) => {
        const p = prestazioniCatalogo.find((x) => String(x.id) === String(prestazioneId));
        setFormPrestazioni((prev) =>
            prev.map((row, i) =>
                i === index
                    ? p
                        ? {
                              prestazione_id: p.id,
                              nome_snapshot: p.nome,
                              prezzo_unitario: p.prezzo_indicativo || 0,
                              iva: p.iva || 0,
                              quantita: row.quantita || 1,
                              costo_prodotto_snapshot: p.costo_prodotto || 0,
                              ammette_esenzione: !!p.ammette_esenzione,
                              attestazione_id: null,
                          }
                        : { ...row, prestazione_id: '', nome_snapshot: '' }
                    : row
            )
        );
    };

    const handleAttestazioneChange = (index, valore) => {
        setFormPrestazioni((prev) =>
            prev.map((row, i) => (i === index
                ? { ...row, attestazione_id: valore === '' ? null : valore }
                : row))
        );
    };

    const handleQuantitaChange = (index, quantita) => {
        setFormPrestazioni((prev) =>
            prev.map((row, i) => (i === index ? { ...row, quantita } : row))
        );
    };

    const handlePrezzoChange = (index, prezzo_unitario) => {
        setFormPrestazioni((prev) =>
            prev.map((row, i) => (i === index ? { ...row, prezzo_unitario } : row))
        );
    };

    /* I tre campi liberi del modulo (data, patologia, note) sono
       elementi non controllati, letti al salvataggio: per il confronto
       si copiano qui a ogni cambiamento del modulo, cosi' entrano
       nell'istantanea come tutto il resto. */
    const [campiLiberi, setCampiLiberi] = useState({});
    const leggiCampiLiberi = (e) => {
        const f = new FormData(e.currentTarget);
        setCampiLiberi({
            data_visita: f.get('data_visita') || '',
            patologia: f.get('patologia') || '',
            note_generali: f.get('note_generali') || '',
        });
    };
    const [versioneModulo, setVersioneModulo] = useState(0);
    const sporco = useSporco(
        showNewVisitaForm
            ? { formMedicoId, formPrestazioni, botoxData, fillerData, campiLiberi }
            : null,
        versioneModulo
    );
    const chiedi = useUscitaProtetta(sporco);

    const resetForm = () => {
        setFormMedicoId(esecutoreImposto ? utente.medico_id : null);
        setFormPrestazioni([]);
        setBotoxData({ valori: {}, punti: [] });
        setFillerData({ valori: {}, zones: [] });
        setConsensiDisponibili([]);
        setFirmaConsenso(true);
        setCampiLiberi({});
        setVersioneModulo((v) => v + 1);
    };

    /* Chiudere il modulo con qualcosa dentro chiede conferma: sfondo, X
       e «Annulla» passano tutti da qui. */
    const chiudiModulo = () => {
        if (!chiedi()) return;
        setShowNewVisitaForm(false);
        resetForm();
    };

    /**
     * Prima riga che ha chiesto una nuova attestazione, se c'e'.
     * Il salvataggio si ferma li' finche' non e' stata firmata.
     */
    const prossimaAttestazione = (righe) =>
        righe.findIndex((r) => r.attestazione_id === 'nuova');

    /**
     * Salvataggio vero. Riceve i dati del form e le righe come
     * argomenti perche' viene richiamato anche dopo la firma
     * dell'attestazione, quando l'evento del form non esiste piu'.
     */
    const salvaVisita = async (dati, righe) => {
        const prestazioniPayload = righe
            .filter((p) => p.nome_snapshot && p.nome_snapshot.trim() !== '')
            .map((p) => ({
                /* L'id della riga, se esisteva gia': e' quello che
                   permette al server di riconciliare invece di
                   cancellare e riscrivere tutto. Una riga nuova non
                   ce l'ha. */
                id: p.id || null,
                prestazione_id: p.prestazione_id || null,
                nome_snapshot: p.nome_snapshot,
                prezzo_unitario: parseFloat(p.prezzo_unitario) || 0,
                iva: parseFloat(p.iva) || 0,
                quantita: parseFloat(p.quantita) || 1,
                costo_prodotto_snapshot: parseFloat(p.costo_prodotto_snapshot) || 0,
                // 'nuova' non e' un id: quell'attestazione non e' stata firmata
                attestazione_id: (p.attestazione_id && p.attestazione_id !== 'nuova')
                    ? p.attestazione_id : null,
            }));

        // 'trattamento' nascosto: auto-popolato dai nomi prestazioni (retrocompatibilita')
        const trattamentoAuto = prestazioniPayload.map((p) => p.nome_snapshot).join(', ');

        try {
            const data = {
                data_visita: dati.data_visita,
                patologia: dati.patologia,
                trattamento: trattamentoAuto || null,
                note_generali: dati.note_generali,
                medico_id: formMedicoId || null,
                prestazioni: prestazioniPayload,
                // Quella scelta in cima al modulo; chi non ha la cartella
                // estetica non la sceglie e non ce l'ha.
                cartella: vedeEstetica ? cartellaNuova : 'mediche',
            };

            const res = await visiteAPI.create(pazienteId, data);
            const visitaCreata = res.data?.data?.visita;

            if (isBotox && res.data.success) {
                const visitaId = visitaCreata.id;
                try {
                    await visiteAPI.createModulo(visitaId, {
                        tipo: 'botox',
                        titolo: 'Botox - ' + new Date().toLocaleDateString('it-IT'),
                        contenuto: botoxData,
                        ordine: 0,
                    });
                } catch (err) {
                    console.error('Errore creazione modulo botox:', err);
                }
            }

            // Consensi da far firmare: catturati prima del reset del form
            const daFirmare = (firmaConsenso && consensiDisponibili.length > 0 && visitaCreata)
                ? {
                    visitaId: visitaCreata.id,
                    medicoId: formMedicoId || null,
                    consensi: consensiDisponibili
                }
                : null;

            setShowNewVisitaForm(false);
            setDatiVisitaInAttesa(null);
            resetForm();
            loadVisite(); annunciaProcedureCambiate();
            /* Se la procedura ha prodotti a lotto obbligatorio si avvisa,
               una volta, che il tagliando va consegnato. Non si blocca
               niente: la coda del magazzino ha comunque la riga. */
            if (visitaCreata) {
                try {
                    const r = await tracciabilitaAPI.daIndicare(visitaCreata.id);
                    setMaterialeInCoda((r.data.data.da_indicare || []).length > 0);
                } catch (e) { /* l'avviso e' una cortesia, non un passaggio */ }
            }
            if (daFirmare) { setDoveSiFirma(null); setConsensoDaFirmare(daFirmare); }
        } catch (error) {
            console.error('Errore creazione procedura:', error);
            notificaErrore(messaggioDa(error, 'Non riesco a creare la procedura'));
        }
    };

    /** L'attestazione e' stata firmata: si aggiorna la riga e si prosegue. */
    const attestazioneCompletata = (attestazioneId) => {
        const indice = attestazioneDaFare.indice;
        const aggiornate = formPrestazioni.map((r, k) =>
            k === indice ? { ...r, attestazione_id: attestazioneId, iva: 0 } : r);
        setFormPrestazioni(aggiornate);
        setAttestazioneDaFare(null);
        loadAttestazioni();

        const altra = prossimaAttestazione(aggiornate);
        if (altra >= 0) {
            setAttestazioneDaFare({ indice: altra, nome: aggiornate[altra].nome_snapshot });
            return;
        }
        if (datiVisitaInAttesa) salvaVisita(datiVisitaInAttesa, aggiornate);
    };

    /**
     * Rinuncia all'attestazione: la prestazione torna imponibile.
     * Meglio toglierla che lasciare il salvataggio a meta'.
     */
    const attestazioneAnnullata = () => {
        const indice = attestazioneDaFare.indice;
        setFormPrestazioni((prev) =>
            prev.map((r, k) => (k === indice ? { ...r, attestazione_id: null } : r)));
        setAttestazioneDaFare(null);
        setDatiVisitaInAttesa(null);
    };

    const handleCreateVisita = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const dati = {};
        formData.forEach((v, k) => { dati[k] = v; });

        // Se serve un'attestazione nuova, prima la si compila e firma
        const daFare = prossimaAttestazione(formPrestazioni);
        if (daFare >= 0) {
            setDatiVisitaInAttesa(dati);
            setAttestazioneDaFare({
                indice: daFare,
                nome: formPrestazioni[daFare].nome_snapshot,
            });
            return;
        }

        salvaVisita(dati, formPrestazioni);
    };

    const handleToggleVisita = (visitaId) => {
        if (expandedVisita === visitaId) {
            setExpandedVisita(null);
        } else {
            setExpandedVisita(visitaId);
            if (!moduliPerVisita[visitaId]) {
                loadModuli(visitaId);
            }
        }
    };

    const handleAddModulo = async (visitaId, tipo) => {
        const modulo = {
            tipo,
            titolo: tipo.charAt(0).toUpperCase() + tipo.slice(1) + ' - ' + new Date().toLocaleDateString('it-IT'),
            contenuto: tipo === 'botox' ? { valori: {}, punti: [] } : {},
            ordine: (moduliPerVisita[visitaId]?.length || 0),
        };

        try {
            await visiteAPI.createModulo(visitaId, modulo);
            loadModuli(visitaId);
        } catch (error) {
            notificaErrore(messaggioDa(error, 'Non riesco a creare il modulo'));
        }
    };

    const handleDeleteVisita = async (id) => {
        if (window.confirm('Eliminare questa procedura? Tutti i moduli e le prestazioni associate verranno eliminati.')) {
            try {
                await visiteAPI.delete(id);
                loadVisite(); annunciaProcedureCambiate();
            } catch (error) {
                notificaErrore(messaggioDa(error, 'Non riesco a eliminare la procedura'));
            }
        }
    };

    const formatEuro = (v) => {
        const n = parseFloat(v);
        if (isNaN(n)) return '€ 0,00';
        return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
    };

    const calcTotaleVisita = (prestazioni) => {
        if (!Array.isArray(prestazioni)) return 0;
        // Stessa aritmetica dei totali del form: due punti che rispondono
        // alla stessa domanda devono dare lo stesso numero.
        return prestazioni.reduce((sum, p) => {
            const riga = imponibileRiga(p.prezzo_unitario, parseFloat(p.quantita) || 0);
            return somma(sum, riga, ivaSu(riga, p.iva));
        }, 0);
    };

    /* Chi vede la cartella estetica: il medico che ce l'ha sulla propria
       scheda, e chiunque veda tutto - amministrazione e segreteria
       fatturano anche quelle. Il campo arriva dal login. */
    const vedeEstetica = utente?.vede_estetica !== false;
    const visiteInCartella = vedeEstetica
        ? visite.filter((v) => (v.cartella || 'mediche') === cartella)
        : visite.filter((v) => (v.cartella || 'mediche') === 'mediche');

    if (loading) {
        return <div className="loading">Caricamento procedure...</div>;
    }

    return (
        <div className="visite-container">
            <div className="visite-header">
                <h2>Procedure mediche</h2>
                <button onClick={() => { setCartellaNuova(cartella); setShowNewVisitaForm(true); setVersioneModulo((v) => v + 1); }}
                        className="btn-primary">
                    Nuova procedura
                </button>
            </div>

            {/* LE DUE CARTELLE
                Non e' un filtro di comodo: una procedura NASCE in una delle
                due, e la nuova si crea in quella aperta. La cartella
                estetica si mostra solo a chi ce l'ha - un ortopedico non ha
                niente da farci - ma non e' una protezione: la cartella
                clinica resta aperta, e a proteggere il dettaglio di una
                procedura estetica e' la riservatezza. */}
            {vedeEstetica && (
                <div className="vt-cartelle">
                    {[['mediche', 'Prestazioni mediche'], ['estetica', 'Medicina estetica']].map(([id, nome]) => (
                        <button key={id} type="button"
                                className={`vt-cartella ${cartella === id ? 'attiva' : ''}`}
                                onClick={() => setCartella(id)}>
                            {nome}
                            <span>{visite.filter((v) => (v.cartella || 'mediche') === id).length}</span>
                        </button>
                    ))}
                </div>
            )}

            {/* IL LOTTO NON SI CHIEDE QUI, E NON E' UNA SEMPLIFICAZIONE
                Prima si apriva un campo appena salvata la procedura,
                "finche' la scatola e' ancora sul tavolo". Ma chi ha la
                scatola in mano ha anche il paziente sul lettino: fermarsi
                a digitare un codice a dodici cifre in quel momento
                significa digitarlo di fretta, e un lotto sbagliato su un
                dispositivo impiantato e' peggio di un lotto indicato
                dopo. Il medico consegna il tagliando alla segreteria, che
                lo registra dalla coda del magazzino leggendolo con calma.
                Il materiale resta in ogni caso segnato come usato: cio'
                che manca e' solo da quale confezione. */}
            {materialeInCoda && (
                <div className="vt-avviso-materiale">
                    <Package size={16} />
                    <div>
                        <b>Materiale tracciato da scaricare.</b>{' '}
                        Consegna il tagliando con il numero di lotto alla segreteria:
                        lo registra lei dalla coda del magazzino. La procedura è già
                        salvata e il prodotto risulta usato.
                        <button className="vt-chiudi-avviso" onClick={() => setMaterialeInCoda(false)}>
                            Ho capito
                        </button>
                    </div>
                </div>
            )}

            {visiteInCartella.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon"><ClipboardList size={38} strokeWidth={1.2} aria-hidden="true" /></div>
                    <h3>Nessuna procedura in questa cartella</h3>
                    <p>Clicca "Nuova procedura" per iniziare</p>
                </div>
            ) : (
                <div className="visite-list">
                    {visiteInCartella.map((visita) => {
                        const prestList = visita.prestazioni || [];
                        const totaleVis = calcTotaleVisita(prestList);
                        return (
                            <div key={visita.id} id={`visita-${visita.id}`} className="visita-card">
                                <div className="visita-row">
                                    <div className="visita-info">
                                        <div className="visita-data">
                                            {new Date(visita.data_visita).toLocaleDateString('it-IT')}
                                        </div>
                                        <div className="visita-dettagli">
                                            <span className="visita-patologia">{visita.patologia}</span>
                                            {visita.medico_cognome && (
                                                <>
                                                    <span className="visita-separator">•</span>
                                                    <span className="visita-medico">Dr. {visita.medico_cognome} {visita.medico_nome}</span>
                                                </>
                                            )}
                                            {prestList.length === 0 && visita.trattamento && (
                                                <>
                                                    <span className="visita-separator">•</span>
                                                    <span className="visita-trattamento">{visita.trattamento}</span>
                                                </>
                                            )}
                                        </div>
                                        {prestList.length > 0 && (
                                            <div className="visita-prestazioni-inline">
                                                {prestList.map((p, idx) => (
                                                    <span key={p.id || idx} className="prest-chip">
                                                        {p.nome_snapshot}
                                                        {parseFloat(p.quantita) !== 1 && <span className="prest-qta"> ×{p.quantita}</span>}
                                                    </span>
                                                ))}
                                                <span className="visita-totale">Totale: <strong>{formatEuro(totaleVis)}</strong></span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="visita-actions">
                                        <button onClick={() => handleToggleVisita(visita.id)} className="btn-expand">
                                            {expandedVisita === visita.id ? '▼' : '▶'}
                                        </button>
                                        <button onClick={() => handleDeleteVisita(visita.id)} className="btn-delete-small" title="Elimina procedura"><Trash2 size={15} strokeWidth={1.8} aria-hidden="true" /></button>
                                    </div>
                                </div>

                                {expandedVisita === visita.id && (
                                    <>
                                        {/* Le richieste da decidere si vedono anche
                                            quando la procedura e' aperta: chi ha eseguito
                                            la vede tutta, e la richiesta e' una cosa che
                                            deve poter sbrigare senza uscire da qui. */}
                                        <RichiesteInAttesa
                                            richieste={daDecidere[visita.id]}
                                            onDecisa={dopoDecisione}
                                        />
                                        {visita.riservata_nascosta && !visiteAperte[visita.id] ? (
                                            <PannelloRiservato
                                                visita={visita}
                                                richiestaInAttesa={(mieInAttesa[visita.id] || [])[0]}
                                                onRichiesta={(r) => setMieInAttesa(
                                                    (p) => ({ ...p, [visita.id]: [r] }))}
                                                onAperta={(v) => setVisiteAperte(
                                                    (p) => ({ ...p, [visita.id]: v }))}
                                            />
                                        ) : (
                                            <ProceduraDettaglio
                                                sessoPaziente={sessoPaziente}
                                                visita={visiteAperte[visita.id] || visita}
                                                moduli={moduliPerVisita[visita.id] || []}
                                                onAddModulo={handleAddModulo}
                                                onReload={() => loadModuli(visita.id)}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {showNewVisitaForm && (
                <div className="modal-overlay" onClick={chiudiModulo}>
                    <div className={(isBotox || isFiller) ? "modal-content modal-botox-wide" : "modal-content modal-procedura"} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Nuova procedura</h2>
                            <button className="btn-close" onClick={chiudiModulo}>✕</button>
                        </div>
                        <form onSubmit={handleCreateVisita} onChange={leggiCampiLiberi}>
                            {/* Il tipo di trattamento sta in cima perche' decide
                                dove la procedura andra' a finire, e perche' da
                                esso dipende cosa si compilera' dentro: la
                                cartella estetica ha gli schemi tecnici, quella
                                delle prestazioni mediche le schede del medico.
                                Compare solo a chi ha tutte e due le cartelle:
                                per gli altri non e' una scelta, e una scelta
                                con una risposta sola e' un clic in piu'. */}
                            {vedeEstetica && (
                                <div className="form-group">
                                    <label>Tipo di trattamento *</label>
                                    <div className="vt-tipo">
                                        {[['mediche', 'Prestazione medica'],
                                          ['estetica', 'Medicina estetica']].map(([id, nome]) => (
                                            <button type="button" key={id}
                                                    className={`vt-tipo-scelta ${cartellaNuova === id ? 'attiva' : ''}`}
                                                    onClick={() => setCartellaNuova(id)}>
                                                {nome}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="form-group">
                                <label>Data Procedura *</label>
                                <input type="date" name="data_visita" required />
                            </div>
                            <div className="form-group">
                                <label>Patologia *</label>
                                <input type="text" name="patologia" placeholder="Es: ACNE" required />
                            </div>

                            <div className="form-group">
                                <label>Medico Esecutore</label>
                                {esecutoreImposto ? (
                                    <div className="esecutore-imposto">
                                        Esecutore: {nomeMedico(medici, utente.medico_id)}
                                    </div>
                                ) : (
                                    <MedicoSelect
                                        value={formMedicoId}
                                        onChange={(medicoId) => setFormMedicoId(medicoId)}
                                        medici={medici}
                                    />
                                )}
                            </div>

                            <div className="form-group">
                                <label>Prestazioni eseguite</label>
                                {formPrestazioni.length === 0 ? (
                                    <div className="prestazioni-empty">
                                        Nessuna prestazione aggiunta.
                                    </div>
                                ) : (
                                    <div className="prestazioni-righe">
                                        {formPrestazioni.map((p, idx) => {
                                            const subtot = (parseFloat(p.prezzo_unitario) || 0) * (parseFloat(p.quantita) || 0);
                                            return (
                                                <div key={idx} className="prestazione-riga">
                                                    <SelettorePrestazione
                                                        voci={prestazioniDisponibili}
                                                        valore={p.prestazione_id}
                                                        onChange={(id) => handlePrestazioneChange(idx, id)}
                                                    />
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={p.prezzo_unitario}
                                                        onChange={(e) => handlePrezzoChange(idx, e.target.value)}
                                                        className="prest-prezzo"
                                                        placeholder="Prezzo"
                                                        title="Prezzo unitario"
                                                    />
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={p.quantita}
                                                        onChange={(e) => handleQuantitaChange(idx, e.target.value)}
                                                        className="prest-qta-input"
                                                        placeholder="Q.tà"
                                                        title="Quantità"
                                                    />
                                                    <span className="prest-iva">IVA {parseFloat(p.iva) || 0}%</span>
                                                    <span className="prest-subtot">{formatEuro(subtot)}</span>
                                                    <button
                                                        type="button"
                                                        className="btn-remove-prest"
                                                        onClick={() => handleRemovePrestazioneRow(idx)}
                                                        title="Rimuovi"
                                                    >
                                                        ✕
                                                    </button>
                                                    {p.ammette_esenzione && (
                                                        <div className="prest-esenzione">
                                                            <label>Esenzione IVA</label>
                                                            <select
                                                                value={p.attestazione_id || ''}
                                                                onChange={(e) => handleAttestazioneChange(idx, e.target.value)}
                                                            >
                                                                <option value="">
                                                                    Nessuna — prestazione imponibile
                                                                </option>
                                                                {attestazioniValide.map((a) => (
                                                                    <option key={a.id} value={a.id}>
                                                                        {a.icd10_codice ? `${a.icd10_codice} — ${a.icd10_titolo}` : a.trattamento}
                                                                        {a.valida_al ? ` (fino al ${new Date(a.valida_al).toLocaleDateString('it-IT')})` : ''}
                                                                    </option>
                                                                ))}
                                                                <option value="nuova">
                                                                    + Nuova attestazione…
                                                                </option>
                                                            </select>
                                                            {p.attestazione_id === 'nuova' && (
                                                                <span className="prest-esenzione-nota">
                                                                    Alla chiusura della procedura andrà compilata e firmata,
                                                                    altrimenti la prestazione resta imponibile.
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                <button type="button" className="btn-add-prest" onClick={handleAddPrestazioneRow}>
                                    Aggiungi prestazione
                                </button>

                                {attestazioneDaFare && (
                                    <ModuloAttestazione
                                        pazienteId={pazienteId}
                                        trattamentoIniziale={attestazioneDaFare.nome}
                                        onCompletata={attestazioneCompletata}
                                        onAnnulla={attestazioneAnnullata}
                                    />
                                )}

                                {formPrestazioni.length > 0 && (
                                    <div className="prestazioni-totali">
                                        <span>Imponibile: <strong>{formatEuro(totaliForm.imponibile)}</strong></span>
                                        <span>IVA: <strong>{formatEuro(totaliForm.iva)}</strong></span>
                                        <span className="totale-finale">Totale: <strong>{formatEuro(totaliForm.totale)}</strong></span>
                                    </div>
                                )}
                            </div>

                            {/* ---------- Consenso informato ---------- */}
                            {consensiDisponibili.length > 0 && (
                                <div className="form-group consenso-box">
                                    <label className="consenso-check">
                                        <input
                                            type="checkbox"
                                            checked={firmaConsenso}
                                            onChange={(e) => setFirmaConsenso(e.target.checked)}
                                        />
                                        <span>
                                            <strong>Compilazione e firma consenso</strong>
                                            <small>
                                                Al salvataggio {consensiDisponibili.length === 1
                                                    ? 'verrà proposto il consenso'
                                                    : `verranno proposti ${consensiDisponibili.length} consensi`} da far firmare al paziente.
                                            </small>
                                        </span>
                                    </label>
                                    <ul className="consenso-elenco">
                                        {consensiDisponibili.map((t) => (
                                            <li key={t.id}>
                                                {t.titolo}
                                                {t._prestazione ? <em> — {t._prestazione}</em> : null}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="form-group">
                                <label>Note Generali</label>
                                <textarea name="note_generali" rows="3" placeholder="Note opzionali..."></textarea>
                            </div>
                            {isBotox && (
                                <div className="botox-section-modal">
                                    <h3>Mappa botox</h3>
                                    <MappaBotox data={botoxData} onChange={setBotoxData} readOnly={false} />
                                </div>
                            )}
                            {isFiller && (
                                <div className="botox-section-modal">
                                    <h3>Mappa Filler</h3>
                                    <MappaFiller data={fillerData} onChange={setFillerData} readOnly={false} />
                                </div>
                            )}
                            <div className="modal-footer">
                                <button type="button" onClick={chiudiModulo} className="btn-secondary">
                                    Annulla
                                </button>
                                <button type="submit" className="btn-primary">
                                    Crea Procedura
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Prima si decide dove firma il paziente, poi si apre
                la schermata giusta. */}
            {consensoDaFirmare && dovesiFirma === null && (
                <SceltaFirma
                    pazienteId={pazienteId}
                    visitaId={consensoDaFirmare.visitaId}
                    medicoId={consensoDaFirmare.medicoId}
                    consensi={consensoDaFirmare.consensi}
                    onSuPc={() => setDoveSiFirma('pc')}
                    onCompletato={() => {
                        setConsensoDaFirmare(null);
                        setDoveSiFirma(null);
                        loadVisite(); annunciaProcedureCambiate();
                    }}
                    onAnnulla={() => {
                        setConsensoDaFirmare(null);
                        setDoveSiFirma(null);
                    }}
                />
            )}

            {consensoDaFirmare && dovesiFirma === 'pc' && (
                <FirmaConsenso
                    pazienteId={pazienteId}
                    visitaId={consensoDaFirmare.visitaId}
                    medicoId={consensoDaFirmare.medicoId}
                    consensi={consensoDaFirmare.consensi}
                    onClose={() => { setConsensoDaFirmare(null); setDoveSiFirma(null); }}
                    onComplete={() => loadVisite()}
                />
            )}
        </div>
    );
};

export default VisiteTab;
